import { Ico_ArrowDown, Ico_ArrowRight } from "@sagecentral/core-ui";
import { forwardRef, useCallback, useImperativeHandle, useState } from "react";
import useAddRelatedColumns from "./useAddRelatedColumns";
import { DataGridPro, GridApiPro, GridRowSelectionModel } from "@mui/x-data-grid-pro";
import { IAddRelatedWorkItemEntry } from "hooks/work-items";
import { GridBox, GridNoRowsOverlay } from "components/shared/grids";
import { Box, Collapse, Grid, Typography } from "@mui/material";

const NoSelectionsOverlay = () => <GridNoRowsOverlay text={`No work items selected.`} />

export interface ISelectedWorkItemsGrid {
    selections: IAddRelatedWorkItemEntry[];
    setSelection: (model: GridRowSelectionModel, workItems: IAddRelatedWorkItemEntry[]) => void;
};

type SelectedWorkItemsGridProps = {
    apiRef: React.MutableRefObject<GridApiPro>;
    setCanSubmit: (value: boolean) => void;
};

const SelectedWorkItemsGrid = forwardRef((props: SelectedWorkItemsGridProps, ref: React.ForwardedRef<ISelectedWorkItemsGrid>) => {
    const {
        apiRef,
        setCanSubmit
    } = props;
    const [expand, setExpand] = useState(false);

    const [
        columns
    ] = useAddRelatedColumns(false, apiRef);

    const [rows, setRows] = useState<IAddRelatedWorkItemEntry[]>([]);

    const setSelection = useCallback((model: GridRowSelectionModel, workItems: IAddRelatedWorkItemEntry[]) => {
        let newRows: IAddRelatedWorkItemEntry[] = [
            ...rows
        ];
        for (let id of model) {
            const existing = newRows.findIndex(w => w.id == +id);
            if (existing !== -1)
                continue;

            const idx = workItems.findIndex(w => w.id == +id);
            if (idx > -1) {
                newRows.push(workItems[idx]);
            }
        }

        for (var i = newRows.length - 1; i >= 0; i--) {
            const existing = newRows[i];
            const idx = model.findIndex(id => existing.id == +id);
            if (idx === -1) {
                newRows.splice(i, 1);
            }
        }
        setRows(newRows);
        setCanSubmit(newRows.length > 0);
    }, [rows]);

    useImperativeHandle(ref, () => ({
        selections: rows,
        setSelection
    }), [setSelection]);

    const getData = () => rows ?? [];

    return (

        <GridBox getData={getData}>
            <Typography 
                display="flex" 
                alignItems="center" 
                fontWeight='600'
                component="div"
                role="heading"
                aria-level={2}
            >
                <Grid 
                    item
                    sx={{
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer'
                    }}
                    onClick={() => setExpand(!expand)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setExpand(!expand);
                        }
                    }}
                    role="button"
                    aria-expanded={expand}
                    aria-controls="selected-work-items-section"
                    tabIndex={0}
                >
                    {expand
                        ? <Ico_ArrowDown color='primary' aria-hidden="true" />
                        : <Ico_ArrowRight color='primary' aria-hidden="true" />}
                    <span style={{ marginLeft: '8px' }}>You have selected the following Work Items:</span>
                </Grid>
            </Typography>
            <Collapse in={expand}>
                <Box 
                    id="selected-work-items-section"
                    sx={theme => ({
                        minHeight: '100px',
                    })}
                    role="region"
                    aria-label="Selected work items"
                >
                    <DataGridPro

                        getRowId={row => row?.id}
                        rows={rows ?? []}
                        columns={columns}
                        hideFooter
                        disableColumnFilter
                        disableColumnMenu
                        sx={theme => ({
                            '& .MuiDataGrid-columnHeaders': {
                                '& .MuiDataGrid-columnHeader': {
                                    backgroundColor: theme.palette.primary.main,
                                    color: theme.palette.primary.contrastText
                                },
                                '& .MuiDataGrid-columnHeaderTitle': {
                                    color: theme.palette.primary.contrastText
                                },
                                '& .MuiButtonBase-root': {
                                    color: 'inherit'
                                }
                            }
                        })}
                        slots={{
                            noRowsOverlay: () => NoSelectionsOverlay(),
                        }}
                    />
                </Box>
            </Collapse>
        </GridBox>
    );
});

export default SelectedWorkItemsGrid;